// D8: once an item's photo is picked, the browser draws it upright on a
// white canvas no larger than 2048 pixels and keeps that, a JPEG of about
// quality 90, to show and to send (#948, owner 2026-10-28: "allow larger
// uploads with proper scaling, cropping, and optomization"). A phone's photo
// of many megabytes then travels as a few hundred kilobytes.
//
// It draws only the photo's centre square, the part the server keeps: the
// server centre-crops an item photo to a square (centreSquare in
// internal/adapter/images), so cropping it here first changes nothing it
// stores. It means the shrink never thins the side the server crops by: a
// 5000 by 100 strip is sent as 100 by 100, never as 2048 by 41, which the
// server would refuse as too small. And the preview shows what the
// catalogue will show.
//
// The server never trusts this step: it checks, crops, fits and encodes
// every file itself (UC-9.R16). So a file the browser cannot draw, or a
// canvas that gives nothing back, is sent as it is, and the server answers
// it as it would any file.

/** The longest side the browser sends, in pixels. */
export const LONGEST_SIDE = 2048;

/** The JPEG quality the browser sends at, from 0 to 1. */
export const JPEG_QUALITY = 0.9;

/**
 * The centre square of a picture, as the server crops an item photo, and
 * the side it is drawn at: within longest, never grown.
 */
export function centreSquare(
	width: number,
	height: number,
	longest = LONGEST_SIDE,
): { x: number; y: number; side: number; drawn: number } {
	const side = Math.min(width, height);
	return {
		x: Math.floor((width - side) / 2),
		y: Math.floor((height - side) / 2),
		side,
		drawn: Math.max(1, Math.min(side, longest)),
	};
}

/**
 * The photo to send: its centre square drawn upright on white, within
 * LONGEST_SIDE, as a JPEG; or the file itself when the browser cannot draw it.
 */
export async function shrinkPhoto(file: Blob): Promise<Blob> {
	let picture: ImageBitmap | undefined;
	try {
		// from-image turns a phone's photo by its EXIF orientation, so what
		// is drawn is what the owner saw.
		picture = await createImageBitmap(file, { imageOrientation: 'from-image' });
		return (await paint(picture, centreSquare(picture.width, picture.height))) ?? file;
	} catch {
		return file;
	} finally {
		picture?.close();
	}
}

type Square = ReturnType<typeof centreSquare>;
type Paint = Pick<CanvasRenderingContext2D, 'fillStyle' | 'fillRect' | 'drawImage'>;

// A transparent PNG is flattened onto white, as the server flattens it, so
// a JPEG, which has no transparency, never turns its clear parts black.
function onWhite(context: Paint, picture: ImageBitmap, square: Square) {
	const { x, y, side, drawn } = square;
	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, drawn, drawn);
	context.drawImage(picture, x, y, side, side, 0, 0, drawn, drawn);
}

async function paint(picture: ImageBitmap, square: Square): Promise<Blob | null> {
	if (typeof OffscreenCanvas !== 'undefined') {
		const canvas = new OffscreenCanvas(square.drawn, square.drawn);
		const context = canvas.getContext('2d');
		if (!context) return null;
		onWhite(context, picture, square);
		return canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY });
	}
	const canvas = document.createElement('canvas');
	canvas.width = square.drawn;
	canvas.height = square.drawn;
	const context = canvas.getContext('2d');
	if (!context) return null;
	onWhite(context, picture, square);
	return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
}
